import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Waybill, Parcel, ParcelStatus, WaybillStatus, WaybillDeliveryMethod, WaybillDeliveryType } from '../interfaces/waybill.interfaces';
import { DataService } from './data.service';
import { environment } from '../../environments/environment';

/**
 * Service for managing waybills operations
 * Handles CRUD operations for waybills based on the Opr_Waybills database table structure
 * Provides conversion between frontend interfaces and database formats
 */
@Injectable({
  providedIn: 'root'
})
export class WaybillService {
  private apiUrl = environment.apiUrl + '/shipment-request';
  constructor(private dataService: DataService, private http: HttpClient) {
    this.initializeDemoData();
    
  }

  /**
   * Get waybills for a specific manifest
   * @param manifestCode The code of the manifest
   * @returns An observable of waybills array
   */  getWaybillsByManifest(manifestCode: string): Observable<Waybill[]> {
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.warn('No manifest details found');
      return of([]);
    }
    
    const manifest = manifestDetails.find(m => m && m.manifestCode === manifestCode);
    
    if (manifest && manifest.waybills && Array.isArray(manifest.waybills)) {
      // Convert to proper Waybill interface with enhanced properties
      return of(this.enhanceWaybills(manifest.waybills));
    }
    
    return of([]);
  }
  
  /**
   * Get a specific waybill by its code
   * @param waybillCode The code of the waybill
   * @returns An observable of the waybill or null if not found
   */  getWaybillByCode(waybillCode: string): Observable<Waybill | null> {
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.warn('No manifest details found');
      return of(null);
    }
    
    // Search through all manifests
    for (const manifest of manifestDetails) {
      if (manifest && manifest.waybills && Array.isArray(manifest.waybills)) {
        const waybill = manifest.waybills.find((w: any) => w && w.code === waybillCode);
        if (waybill) {
          // Convert to proper Waybill interface
          return of(this.enhanceWaybill(waybill));
        }
      }
    }
    
    return of(null);
  }
    /**
   * Get all parcels for a specific waybill
   * @param waybillCode The code of the waybill
   * @returns An observable of parcels array
   */
  getParcelsByWaybill(waybillCode: string): Observable<Parcel[]> {
    // Since the new Waybill interface doesn't include parcels directly,
    // we need to get them from the manifest data structure
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.warn('No manifest details found');
      return of([]);
    }
    
    // Search through all manifests to find waybill and its parcels
    for (const manifest of manifestDetails) {
      if (manifest && manifest.waybills && Array.isArray(manifest.waybills)) {
        const waybill = manifest.waybills.find((w: any) => w && w.code === waybillCode);
        if (waybill && waybill.parcels) {
          return of(this.enhanceParcels(waybill.parcels));
        }
      }
    }
    
    return of([]);
  }
  
  /**
   * Remove a parcel from a waybill
   * @param waybillCode The code of the waybill
   * @param parcelCode The code of the parcel to remove
   * @returns True if the parcel was removed, false otherwise
   */  removeParcel(waybillCode: string, parcelCode: string): boolean {
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    // If manifestDetails is undefined, return false
    if (!manifestDetails) {
      console.error('No manifest details found');
      return false;
    }
    
    // Find the waybill in any manifest
    for (let i = 0; i < manifestDetails.length; i++) {
      const manifest = manifestDetails[i];
      
      if (!manifest || !manifest.waybills) {
        continue;
      }
      
      for (let j = 0; j < manifest.waybills.length; j++) {
        const waybill = manifest.waybills[j];
        
        if (waybill.code === waybillCode) {
          // Find and remove the parcel
          const parcelIndex = waybill.parcels.findIndex((p: any) => p.code === parcelCode);
          
          if (parcelIndex !== -1) {
            waybill.parcels.splice(parcelIndex, 1);
            
            // Update the parcels count in the manifest summary
            manifest.summary.parcels--;
            
            // Persist changes
            this.dataService.setData('manifest_details', manifestDetails);
            return true;
          }
        }
      }
    }
    
    return false;
  }
  
  /**
   * Relabel a parcel with new information
   * @param waybillCode The code of the waybill
   * @param parcelCode The code of the parcel to relabel
   * @param newData The new data for the parcel
   * @returns True if the parcel was relabeled, false otherwise
   */  relabelParcel(waybillCode: string, parcelCode: string, newData: Partial<Parcel>): boolean {
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    // If manifestDetails is undefined, return false
    if (!manifestDetails) {
      console.error('No manifest details found');
      return false;
    }
    
    // Find the waybill in any manifest
    for (const manifest of manifestDetails) {
      if (!manifest || !manifest.waybills) {
        continue;
      }
      
      for (const waybill of manifest.waybills) {
        if (!waybill || !waybill.parcels) {
          continue;
        }
        
        if (waybill.code === waybillCode) {
          // Find the parcel
          const parcel = waybill.parcels.find((p: any) => p.code === parcelCode);
          
          if (parcel) {
            // Update the parcel with new data
            Object.assign(parcel, newData);
            
            // Persist changes
            this.dataService.setData('manifest_details', manifestDetails);
            return true;
          }
        }
      }
    }
    
    return false;
  }
  
  /**
   * Add parcel action event (audit log)
   * @param waybillCode The code of the waybill
   * @param parcelCode The code of the parcel
   * @param action The action performed
   * @param details Additional details about the action
   */
  logParcelAction(waybillCode: string, parcelCode: string, action: string, details?: string): void {
    // In a real app, this would send the action to a server or store it locally
    console.log(`Parcel action: ${action} on ${parcelCode} of waybill ${waybillCode}`, details);
  }
  
  /**
   * Helper method to enhance waybill objects with additional properties
   * @param waybills Array of basic waybills
   * @returns Enhanced waybills with proper interface and statuses
   */  private enhanceWaybills(waybills: any[]): Waybill[] {
    if (!waybills || !Array.isArray(waybills)) {
      return [];
    }
    return waybills.filter(waybill => waybill).map(waybill => this.enhanceWaybill(waybill));
  }
    /**
   * Helper method to enhance a single waybill object
   * @param waybill Basic waybill object
   * @returns Enhanced waybill with proper interface and status
   */
  private enhanceWaybill(waybill: any): Waybill {
    // Generate a random status for demo purposes
    const statusValues = Object.values(WaybillStatus);
    const randomStatus = statusValues[Math.floor(Math.random() * statusValues.length)];
    
    // Generate random delivery method and type for demo purposes
    const deliveryMethods = Object.values(WaybillDeliveryMethod);
    const deliveryTypes = Object.values(WaybillDeliveryType);
    const randomDeliveryMethod = deliveryMethods[Math.floor(Math.random() * deliveryMethods.length)];
    const randomDeliveryType = deliveryTypes[Math.floor(Math.random() * deliveryTypes.length)];
    
    // Generate demo dates
    const createdDate = new Date(Date.now() - 86400000 * Math.floor(Math.random() * 30));
    const estimatedDelivery = new Date(Date.now() + 86400000 * Math.floor(Math.random() * 5));
    
    return {
      // Primary identifiers
      waybillId: Math.floor(Math.random() * 100000) + 1,
      waybillCode: waybill.code || `WB-${Date.now()}`,
      waybillPalletCode: waybill.palletCode || undefined,
      waybillShipmentCode: waybill.shipmentCode || `SH-${Date.now()}`,
      waybillTrackingNumber: waybill.trackingNumber || `TRK-${waybill.code || Date.now()}`,
      
      // Dates
      waybillDate: waybill.date || createdDate.toISOString(),
      waybillEstimatedDeliveryDate: waybill.estimatedDelivery || estimatedDelivery.toISOString(),
      waybillActualDeliveryDate: waybill.actualDeliveryDate || undefined,
      waybillOriginalDeliveryDate: waybill.originalDeliveryDate || undefined,
      waybillCreatedAt: createdDate.toISOString(),
      waybillUpdatedAt: new Date().toISOString(),
      
      // Delivery information
      waybillDeliveryMethod: randomDeliveryMethod,
      waybillDeliveryType: randomDeliveryType,
      waybillTransitTimeHours: Math.floor(Math.random() * 72) + 2,
      
      // Location information
      waybillOriginLocation: waybill.originLocation || 'Milano Hub',
      waybillDestinationLocation: waybill.address || waybill.destinationLocation || 'Unknown',
      waybillLastKnownLocation: waybill.location || waybill.lastKnownLocation || 'In Transit',
      waybillLatitude: waybill.latitude || (45.4642 + (Math.random() - 0.5) * 0.1),
      waybillLongitude: waybill.longitude || (9.1900 + (Math.random() - 0.5) * 0.1),
      waybillOriginalLatitude: waybill.originalLatitude || undefined,
      waybillOriginalLongitude: waybill.originalLongitude || undefined,
      
      // Cargo information
      waybillTotalWeight: waybill.totalWeight || Math.floor(Math.random() * 100) + 1,
      waybillTotalVolume: waybill.totalVolume || Math.floor(Math.random() * 10) + 0.1,
      waybillFragile: waybill.fragile === true || waybill.fragile === 'Y' || Math.random() > 0.8,
      waybillHazardousMaterial: waybill.hazardousMaterial === true || waybill.hazardousMaterial === 'Y' || Math.random() > 0.95,
      waybillCustomsClearanceRequired: waybill.customsClearanceRequired === true || waybill.customsClearanceRequired === 'Y' || Math.random() > 0.7,
      
      // Business information
      waybillBrandCode: waybill.brandCode || 'BRT',
      waybillDealerCode: waybill.dealerCode || 'DEAL001',
      waybillCarrierCode: waybill.carrierCode || 'CARR001',
      
      // Status and metadata
      waybillStatus: randomStatus,
      waybillUpdatedBy: waybill.updatedBy || 'system',
      waybillNote: waybill.notes || waybill.note || 'No special instructions',
      waybillFile: waybill.file || undefined,
      waybillVersion: waybill.version || 1
    };
  }
  
  /**
   * Helper method to enhance parcel objects with additional properties
   * @param parcels Array of basic parcels
   * @returns Enhanced parcels with proper interface and statuses
   */  private enhanceParcels(parcels: any[]): Parcel[] {
    if (!parcels || !Array.isArray(parcels)) {
      return [];
    }
    return parcels.filter(parcel => parcel).map(parcel => this.enhanceParcel(parcel));
  }    /**
   * Helper method to enhance a single parcel object based on Opr_Parcels
   * @param parcel Basic parcel object
   * @returns Enhanced parcel with proper interface and status based on Opr_Parcels
   */
  private enhanceParcel(parcel: any): Parcel {
    // Generate a random status for demo purposes
    const statusValues = Object.values(ParcelStatus);
    const randomStatus = statusValues[Math.floor(Math.random() * statusValues.length)];
    
    // Generate demo dates
    const createdDate = new Date(Date.now() - 86400000 * Math.floor(Math.random() * 30));
    const estimatedDelivery = new Date(Date.now() + 86400000 * Math.floor(Math.random() * 5));
    
    // Convert weight to number if it's a string
    let weight = parcel.weight || parcel.parcelWeight;
    if (typeof weight === 'string' && weight.includes('kg')) {
      weight = parseFloat(weight.replace('kg', '').trim()) || 0;
    } else if (typeof weight === 'string') {
      weight = parseFloat(weight) || 0;
    }
    
    return {
      // Primary identifiers
      parcelId: parcel.parcelId || Math.floor(Math.random() * 100000) + 1,
      parcelCode: parcel.parcelCode || parcel.code || `PRC-${Date.now()}`,
      parcelWaybillCode: parcel.parcelWaybillCode || parcel.waybillCode || undefined,
      parcelPalletCode: parcel.parcelPalletCode || parcel.palletCode || undefined,
      parcelShipmentCode: parcel.parcelShipmentCode || parcel.shipmentCode || `SH-${Date.now()}`,
      parcelBrandCode: parcel.parcelBrandCode || parcel.brandCode || 'BRT',
      parcelDealerCode: parcel.parcelDealerCode || parcel.dealerCode || 'DEAL001',
      parcelBarcode: parcel.parcelBarcode || parcel.barcode || `BC-${Date.now()}`,
      parcelTrackingNumber: parcel.parcelTrackingNumber || parcel.trackingNumber || `TRK-${Date.now()}`,
      
      // Dates
      parcelDate: parcel.parcelDate || parcel.date || createdDate.toISOString(),
      parcelDeliveryDate: parcel.parcelDeliveryDate || parcel.deliveryDate || undefined,
      parcelEstimatedDeliveryDate: parcel.parcelEstimatedDeliveryDate || parcel.estimatedDelivery || estimatedDelivery.toISOString(),
      parcelOriginalDeliveryDate: parcel.parcelOriginalDeliveryDate || parcel.originalDeliveryDate || undefined,
      parcelLastEventDate: parcel.parcelLastEventDate || undefined,
      parcelTakeInChargeDate: parcel.parcelTakeInChargeDate || undefined,
      parcelLastUpdateAt: parcel.parcelLastUpdateAt || undefined,
      parcelUpdatedAt: parcel.parcelUpdatedAt || new Date().toISOString(),
      parcelCreatedAt: parcel.parcelCreatedAt || createdDate.toISOString(),
      
      // Delivery information
      parcelDeliveryStatus: parcel.parcelDeliveryStatus || parcel.deliveryStatus || 'PENDING',
      parcelDeliveryMethod: parcel.parcelDeliveryMethod || parcel.deliveryMethod || 'STANDARD',
      parcelDeliveryType: parcel.parcelDeliveryType || parcel.deliveryType || 'DOOR_TO_DOOR',
      parcelDeliveryMaterial: parcel.parcelDeliveryMaterial || parcel.deliveryMaterial || undefined,
      parcelDeliveryReason: parcel.parcelDeliveryReason || undefined,
      parcelTakeInChargeReason: parcel.parcelTakeInChargeReason || undefined,
      
      // Physical properties
      parcelDescription: parcel.parcelDescription || parcel.description || parcel.content || 'Standard parcel',
      parcelWeight: weight || Math.floor(Math.random() * 100) + 1,
      parcelVolume: parcel.parcelVolume || parcel.volume || Math.floor(Math.random() * 10) + 0.1,
      parcelLength: parcel.parcelLength || parcel.length || Math.floor(Math.random() * 100) + 20,
      parcelWidth: parcel.parcelWidth || parcel.width || Math.floor(Math.random() * 60) + 20,
      parcelHeight: parcel.parcelHeight || parcel.height || Math.floor(Math.random() * 80) + 20,
      parcelType: parcel.parcelType || parcel.type || this.determinePalletType(parcel.parcelDescription || parcel.content),
      
      // Location information
      parcelLocation: parcel.parcelLocation || parcel.location || 'Warehouse',
      parcelLatitude: parcel.parcelLatitude || parcel.latitude || (45.4642 + (Math.random() - 0.5) * 0.1),
      parcelLongitude: parcel.parcelLongitude || parcel.longitude || (9.1900 + (Math.random() - 0.5) * 0.1),
      parcelOriginalLatitude: parcel.parcelOriginalLatitude || parcel.originalLatitude || undefined,
      parcelOriginalLongitude: parcel.parcelOriginalLongitude || parcel.originalLongitude || undefined,
      
      // Special handling flags (Y/N strings from database)
      parcelFragile: parcel.parcelFragile || (this.isFragileContent(parcel.parcelDescription || parcel.content) ? 'Y' : 'N'),
      parcelHazardousMaterial: parcel.parcelHazardousMaterial || (Math.random() > 0.95 ? 'Y' : 'N'),
      parcelTemperatureSensitive: parcel.parcelTemperatureSensitive || (Math.random() > 0.9 ? 'Y' : 'N'),
      parcelHighValueItem: parcel.parcelHighValueItem || (Math.random() > 0.85 ? 'Y' : 'N'),
      parcelRestrictedItem: parcel.parcelRestrictedItem || (Math.random() > 0.95 ? 'Y' : 'N'),
      parcelCustomsClearanceRequired: parcel.parcelCustomsClearanceRequired || (Math.random() > 0.7 ? 'Y' : 'N'),
      parcelDeliveryDateIsForced: parcel.parcelDeliveryDateIsForced || 'N',
      parcelDeliveryDateIsUpdated: parcel.parcelDeliveryDateIsUpdated || 'N',
      parcelDeliveryCoordinatesIsForced: parcel.parcelDeliveryCoordinatesIsForced || 'N',
      parcelDeliveryCoordinatesIsUpdated: parcel.parcelDeliveryCoordinatesIsUpdated || 'N',
      
      // Financial information
      parcelInsuredValue: parcel.parcelInsuredValue || parcel.insuredValue || Math.floor(Math.random() * 1000) + 100,
      
      // Status and metadata
      parcelStatus: parcel.parcelStatus || randomStatus,
      parcelVersion: parcel.parcelVersion || parcel.version || 1,
      parcelLastUpdateBy: parcel.parcelLastUpdateBy || parcel.lastUpdateBy || 'system',
      parcelUpdatedBy: parcel.parcelUpdatedBy || parcel.updatedBy || 'system'
    };
  }
    /**
   * Determines the type of pallet based on its content
   * @param content The content description of the pallet
   * @returns The determined pallet type
   */
  private determinePalletType(content?: string): string {
    if (!content) return 'STANDARD';
    
    content = content.toLowerCase();
    
    if (content.includes('elettronic') || content.includes('tech')) {
      return 'ELECTRONICS';
    } else if (content.includes('abbigliam') || content.includes('cloth') || content.includes('vestiti')) {
      return 'CLOTHING';
    } else if (content.includes('libri') || content.includes('book')) {
      return 'BOOKS';
    } else if (content.includes('aliment') || content.includes('food') || content.includes('cibo')) {
      return 'FOOD';
    } else if (content.includes('cosmet') || content.includes('makeup') || content.includes('trucco')) {
      return 'COSMETICS';
    } else if (content.includes('document') || content.includes('carta')) {
      return 'DOCUMENTS';
    } else if (content.includes('medic') || content.includes('pharma')) {
      return 'MEDICAL';
    } else if (content.includes('fragil')) {
      return 'FRAGILE';
    } else {
      return 'STANDARD';
    }
  }

  /**
   * Determines if content indicates fragile items
   * @param content The content description
   * @returns True if content indicates fragile items
   */
  private isFragileContent(content?: string): boolean {
    if (!content) return false;
    
    content = content.toLowerCase();
    
    return content.includes('fragil') || 
           content.includes('elettronic') || 
           content.includes('tech') ||
           content.includes('cosmet') ||
           content.includes('vetro') ||
           content.includes('glass') ||
           content.includes('ceramica') ||
           content.includes('ceramic');
  }
    /**
   * Generates tags for a parcel based on its content (legacy method)
   * @param content The content description of the parcel
   * @returns An array of tags
   */
  private generateTags(content?: string): string[] {
    if (!content) return ['standard'];
    
    const tags = ['pallet'];
    const type = this.determinePalletType(content);
    
    tags.push(type.toLowerCase());
    
    if (this.isFragileContent(content)) {
      tags.push('fragile');
    }
    
    if (type === 'FOOD') {
      tags.push('perishable');
    }
    
    return tags;
  }
  
  /**
   * Initialize demo data for waybills and parcels
   * This enriches the existing data with more detailed information
   */
  private initializeDemoData(): void {
    // The data is already initialized in the DataService
    // This method would be used to add additional data or enhancements
  }

  /**
   * Create a new waybill
   * @param waybillData The data for the new waybill
   * @returns An observable of the created waybill
   */
  createWaybill(waybillData: any): Observable<Waybill> {
    const newWaybill = this.enhanceWaybill(waybillData);
    
    // In a real application, this would make an HTTP call to create the waybill
    console.log('Creating new waybill:', newWaybill);
    
    return of(newWaybill);
  }

  /**
   * Update an existing waybill
   * @param waybillId The ID of the waybill to update
   * @param updateData The data to update
   * @returns An observable of the updated waybill
   */
  updateWaybill(waybillId: number, updateData: Partial<Waybill>): Observable<Waybill | null> {
    // In a real application, this would make an HTTP call to update the waybill
    console.log('Updating waybill:', waybillId, updateData);
    
    // For demo purposes, we'll try to find and update the waybill in manifest data
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.warn('No manifest details found');
      return of(null);
    }
    
    // Search through all manifests to find the waybill
    for (const manifest of manifestDetails) {
      if (manifest && manifest.waybills && Array.isArray(manifest.waybills)) {
        const waybillIndex = manifest.waybills.findIndex((w: any) => w && w.code === updateData.waybillCode);
        if (waybillIndex !== -1) {
          // Update the waybill data
          Object.assign(manifest.waybills[waybillIndex], updateData);
          
          // Persist changes
          this.dataService.setData('manifest_details', manifestDetails);
          
          // Return the enhanced updated waybill
          return of(this.enhanceWaybill(manifest.waybills[waybillIndex]));
        }
      }
    }
    
    return of(null);
  }

  /**
   * Get waybill by ID
   * @param waybillId The ID of the waybill
   * @returns An observable of the waybill or null if not found
   */
  getWaybillById(waybillId: number): Observable<Waybill | null> {
    // In a real application, this would make an HTTP call using the ID
    console.log('Getting waybill by ID:', waybillId);
    
    // For demo purposes, we'll search by matching enhanced waybill IDs
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.warn('No manifest details found');
      return of(null);
    }
    
    // Search through all manifests
    for (const manifest of manifestDetails) {
      if (manifest && manifest.waybills && Array.isArray(manifest.waybills)) {
        for (const waybill of manifest.waybills) {
          if (waybill) {
            const enhancedWaybill = this.enhanceWaybill(waybill);
            if (enhancedWaybill.waybillId === waybillId) {
              return of(enhancedWaybill);
            }
          }
        }
      }
    }
    
    return of(null);
  }

  /**
   * Update waybill status
   * @param waybillCode The code of the waybill
   * @param newStatus The new status
   * @param updatedBy Who updated the status
   * @returns True if status was updated, false otherwise
   */
  updateWaybillStatus(waybillCode: string, newStatus: string, updatedBy?: string): boolean {
    const manifestDetails = this.dataService.getData<any[]>('manifest_details');
    
    if (!manifestDetails) {
      console.error('No manifest details found');
      return false;
    }
    
    // Find the waybill in any manifest
    for (const manifest of manifestDetails) {
      if (!manifest || !manifest.waybills) {
        continue;
      }
      
      const waybill = manifest.waybills.find((w: any) => w && w.code === waybillCode);
      if (waybill) {
        waybill.status = newStatus;
        waybill.updatedAt = new Date().toISOString();
        if (updatedBy) {
          waybill.updatedBy = updatedBy;
        }
        
        // Persist changes
        this.dataService.setData('manifest_details', manifestDetails);
        
        console.log(`Waybill ${waybillCode} status updated to ${newStatus}`);
        return true;
      }
    }
    
    return false;
  }

  /**
   * Convert boolean to database string format (Y/N)
   * @param value Boolean value
   * @returns 'Y' for true, 'N' for false
   */
  private booleanToDbString(value: boolean): string {
    return value ? 'Y' : 'N';
  }

  /**
   * Convert database string format (Y/N) to boolean
   * @param value Database string value
   * @returns true for 'Y', false for 'N' or any other value
   */
  private dbStringToBoolean(value: string): boolean {
    return value === 'Y';
  }

  /**
   * Prepare waybill data for database submission
   * @param waybill Waybill object
   * @returns Object formatted for database
   */
  prepareDatabaseFormat(waybill: Waybill): any {
    return {
      waybill_id: waybill.waybillId,
      waybill_code: waybill.waybillCode,
      waybill_pallet_code: waybill.waybillPalletCode,
      waybill_shipment_code: waybill.waybillShipmentCode,
      waybill_date: waybill.waybillDate,
      waybill_estimated_delivery_date: waybill.waybillEstimatedDeliveryDate,
      waybill_actual_delivery_date: waybill.waybillActualDeliveryDate,
      waybill_delivery_method: waybill.waybillDeliveryMethod,
      waybill_delivery_type: waybill.waybillDeliveryType,
      waybill_transit_time_hours: waybill.waybillTransitTimeHours,
      waybill_total_weight: waybill.waybillTotalWeight,
      waybill_total_volume: waybill.waybillTotalVolume,
      waybill_brand_code: waybill.waybillBrandCode,
      waybill_dealer_code: waybill.waybillDealerCode,
      waybill_carrier_code: waybill.waybillCarrierCode,
      waybill_origin_location: waybill.waybillOriginLocation,
      waybill_destination_location: waybill.waybillDestinationLocation,
      waybill_tracking_number: waybill.waybillTrackingNumber,
      waybill_last_known_location: waybill.waybillLastKnownLocation,
      waybill_status: waybill.waybillStatus,
      waybill_fragile: this.booleanToDbString(waybill.waybillFragile),
      waybill_hazardous_material: this.booleanToDbString(waybill.waybillHazardousMaterial),
      waybill_customs_clearance_required: this.booleanToDbString(waybill.waybillCustomsClearanceRequired),
      waybill_updated_at: waybill.waybillUpdatedAt,
      waybill_updated_by: waybill.waybillUpdatedBy,
      waybill_note: waybill.waybillNote,
      waybill_file: waybill.waybillFile,
      waybill_version: waybill.waybillVersion,
      waybill_created_at: waybill.waybillCreatedAt,
      waybill_latitude: waybill.waybillLatitude,
      waybill_longitude: waybill.waybillLongitude,
      waybill_original_delivery_date: waybill.waybillOriginalDeliveryDate,
      waybill_original_latitude: waybill.waybillOriginalLatitude,
      waybill_original_longitude: waybill.waybillOriginalLongitude
    };
  }

  /**
   * Parse waybill data from database format
   * @param dbData Database formatted object
   * @returns Waybill object
   */
  parseFromDatabase(dbData: any): Waybill {
    return {
      waybillId: dbData.waybill_id,
      waybillCode: dbData.waybill_code,
      waybillPalletCode: dbData.waybill_pallet_code,
      waybillShipmentCode: dbData.waybill_shipment_code,
      waybillDate: dbData.waybill_date,
      waybillEstimatedDeliveryDate: dbData.waybill_estimated_delivery_date,
      waybillActualDeliveryDate: dbData.waybill_actual_delivery_date,
      waybillDeliveryMethod: dbData.waybill_delivery_method,
      waybillDeliveryType: dbData.waybill_delivery_type,
      waybillTransitTimeHours: dbData.waybill_transit_time_hours,
      waybillTotalWeight: dbData.waybill_total_weight,
      waybillTotalVolume: dbData.waybill_total_volume,
      waybillBrandCode: dbData.waybill_brand_code,
      waybillDealerCode: dbData.waybill_dealer_code,
      waybillCarrierCode: dbData.waybill_carrier_code,
      waybillOriginLocation: dbData.waybill_origin_location,
      waybillDestinationLocation: dbData.waybill_destination_location,
      waybillTrackingNumber: dbData.waybill_tracking_number,
      waybillLastKnownLocation: dbData.waybill_last_known_location,
      waybillStatus: dbData.waybill_status,
      waybillFragile: this.dbStringToBoolean(dbData.waybill_fragile),
      waybillHazardousMaterial: this.dbStringToBoolean(dbData.waybill_hazardous_material),
      waybillCustomsClearanceRequired: this.dbStringToBoolean(dbData.waybill_customs_clearance_required),
      waybillUpdatedAt: dbData.waybill_updated_at,
      waybillUpdatedBy: dbData.waybill_updated_by,
      waybillNote: dbData.waybill_note,
      waybillFile: dbData.waybill_file,
      waybillVersion: dbData.waybill_version,
      waybillCreatedAt: dbData.waybill_created_at,
      waybillLatitude: dbData.waybill_latitude,
      waybillLongitude: dbData.waybill_longitude,
      waybillOriginalDeliveryDate: dbData.waybill_original_delivery_date,
      waybillOriginalLatitude: dbData.waybill_original_latitude,
      waybillOriginalLongitude: dbData.waybill_original_longitude
    };
  }
  /**
   * Prepare parcel data for database submission
   * @param parcel Parcel object
   * @returns Object formatted for database
   */
  prepareParcelDatabaseFormat(parcel: Parcel): any {
    return {
      parcel_id: parcel.parcelId,
      parcel_code: parcel.parcelCode,
      parcel_waybill_code: parcel.parcelWaybillCode,
      parcel_pallet_code: parcel.parcelPalletCode,
      parcel_shipment_code: parcel.parcelShipmentCode,
      parcel_brand_code: parcel.parcelBrandCode,
      parcel_dealer_code: parcel.parcelDealerCode,
      parcel_barcode: parcel.parcelBarcode,
      parcel_tracking_number: parcel.parcelTrackingNumber,
      parcel_date: parcel.parcelDate,
      parcel_delivery_date: parcel.parcelDeliveryDate,
      parcel_estimated_delivery_date: parcel.parcelEstimatedDeliveryDate,
      parcel_original_delivery_date: parcel.parcelOriginalDeliveryDate,
      parcel_last_event_date: parcel.parcelLastEventDate,
      parcel_take_in_charge_date: parcel.parcelTakeInChargeDate,
      parcel_delivery_status: parcel.parcelDeliveryStatus,
      parcel_delivery_method: parcel.parcelDeliveryMethod,
      parcel_delivery_type: parcel.parcelDeliveryType,
      parcel_delivery_material: parcel.parcelDeliveryMaterial,
      parcel_delivery_reason: parcel.parcelDeliveryReason,
      parcel_take_in_charge_reason: parcel.parcelTakeInChargeReason,
      parcel_description: parcel.parcelDescription,
      parcel_weight: parcel.parcelWeight,
      parcel_volume: parcel.parcelVolume,
      parcel_length: parcel.parcelLength,
      parcel_width: parcel.parcelWidth,
      parcel_height: parcel.parcelHeight,
      parcel_type: parcel.parcelType,
      parcel_location: parcel.parcelLocation,
      parcel_latitude: parcel.parcelLatitude,
      parcel_longitude: parcel.parcelLongitude,
      parcel_original_latitude: parcel.parcelOriginalLatitude,
      parcel_original_longitude: parcel.parcelOriginalLongitude,
      parcel_fragile: parcel.parcelFragile,
      parcel_hazardous_material: parcel.parcelHazardousMaterial,
      parcel_temperature_sensitive: parcel.parcelTemperatureSensitive,
      parcel_high_value_item: parcel.parcelHighValueItem,
      parcel_restricted_item: parcel.parcelRestrictedItem,
      parcel_customs_clearance_required: parcel.parcelCustomsClearanceRequired,
      parcel_delivery_date_is_forced: parcel.parcelDeliveryDateIsForced,
      parcel_delivery_date_is_updated: parcel.parcelDeliveryDateIsUpdated,
      parcel_delivery_coordinates_is_forced: parcel.parcelDeliveryCoordinatesIsForced,
      parcel_delivery_coordinates_is_updated: parcel.parcelDeliveryCoordinatesIsUpdated,
      parcel_insured_value: parcel.parcelInsuredValue,
      parcel_status: parcel.parcelStatus,
      parcel_version: parcel.parcelVersion,
      parcel_last_update_at: parcel.parcelLastUpdateAt,
      parcel_last_update_by: parcel.parcelLastUpdateBy,
      parcel_updated_at: parcel.parcelUpdatedAt,
      parcel_updated_by: parcel.parcelUpdatedBy,
      parcel_created_at: parcel.parcelCreatedAt
    };
  }
  /**
   * Parse parcel data from database format
   * @param dbData Database formatted object
   * @returns Parcel object
   */
  parseParcelFromDatabase(dbData: any): Parcel {
    return {
      parcelId: dbData.parcel_id,
      parcelCode: dbData.parcel_code,
      parcelWaybillCode: dbData.parcel_waybill_code,
      parcelPalletCode: dbData.parcel_pallet_code,
      parcelShipmentCode: dbData.parcel_shipment_code,
      parcelBrandCode: dbData.parcel_brand_code,
      parcelDealerCode: dbData.parcel_dealer_code,
      parcelBarcode: dbData.parcel_barcode,
      parcelTrackingNumber: dbData.parcel_tracking_number,
      parcelDate: dbData.parcel_date,
      parcelDeliveryDate: dbData.parcel_delivery_date,
      parcelEstimatedDeliveryDate: dbData.parcel_estimated_delivery_date,
      parcelOriginalDeliveryDate: dbData.parcel_original_delivery_date,
      parcelLastEventDate: dbData.parcel_last_event_date,
      parcelTakeInChargeDate: dbData.parcel_take_in_charge_date,
      parcelLastUpdateAt: dbData.parcel_last_update_at,
      parcelUpdatedAt: dbData.parcel_updated_at,
      parcelCreatedAt: dbData.parcel_created_at,
      parcelDeliveryStatus: dbData.parcel_delivery_status,
      parcelDeliveryMethod: dbData.parcel_delivery_method,
      parcelDeliveryType: dbData.parcel_delivery_type,
      parcelDeliveryMaterial: dbData.parcel_delivery_material,
      parcelDeliveryReason: dbData.parcel_delivery_reason,
      parcelTakeInChargeReason: dbData.parcel_take_in_charge_reason,
      parcelDescription: dbData.parcel_description,
      parcelWeight: dbData.parcel_weight,
      parcelVolume: dbData.parcel_volume,
      parcelLength: dbData.parcel_length,
      parcelWidth: dbData.parcel_width,
      parcelHeight: dbData.parcel_height,
      parcelType: dbData.parcel_type,
      parcelLocation: dbData.parcel_location,
      parcelLatitude: dbData.parcel_latitude,
      parcelLongitude: dbData.parcel_longitude,
      parcelOriginalLatitude: dbData.parcel_original_latitude,
      parcelOriginalLongitude: dbData.parcel_original_longitude,
      parcelFragile: dbData.parcel_fragile,
      parcelHazardousMaterial: dbData.parcel_hazardous_material,
      parcelTemperatureSensitive: dbData.parcel_temperature_sensitive,
      parcelHighValueItem: dbData.parcel_high_value_item,
      parcelRestrictedItem: dbData.parcel_restricted_item,
      parcelCustomsClearanceRequired: dbData.parcel_customs_clearance_required,
      parcelDeliveryDateIsForced: dbData.parcel_delivery_date_is_forced,
      parcelDeliveryDateIsUpdated: dbData.parcel_delivery_date_is_updated,
      parcelDeliveryCoordinatesIsForced: dbData.parcel_delivery_coordinates_is_forced,
      parcelDeliveryCoordinatesIsUpdated: dbData.parcel_delivery_coordinates_is_updated,
      parcelInsuredValue: dbData.parcel_insured_value,
      parcelStatus: dbData.parcel_status,
      parcelVersion: dbData.parcel_version,
      parcelLastUpdateBy: dbData.parcel_last_update_by,
      parcelUpdatedBy: dbData.parcel_updated_by
    };
  }

  /**
   * Download waybill PDF from backend
   * @param waybillCode The code of the waybill
   * @param waybillShipmentCode The shipment code of the waybill
   * @returns An observable of the PDF blob
   */
  downloadWaybillPdf(waybillCode: string, waybillShipmentCode: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/waybill-print/${waybillCode}/${waybillShipmentCode}`, {
      responseType: 'blob'
    });
  }

  exportDataToCeva(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/exportDataToCeva`, {});
  }

  /**
   * Utility method to download the PDF
   * @param waybillCode The code of the waybill
   * @param waybillShipmentCode The shipment code of the waybill
   */
  downloadPdf(waybillCode: string, waybillShipmentCode: string): void {
    this.downloadWaybillPdf(waybillCode, waybillShipmentCode).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `waybill_${waybillCode}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Errore durante il download del PDF:', error);
      }
    });
  }
}
